import {
  ref,
  readonly,
  onBeforeUnmount,
  getCurrentInstance,
  type Ref,
} from 'vue'

import {
  FE_ERROR_GPT_ACCESS_REVOKED,
  FE_ERROR_GPT_NOT_READY,
  GPT_ACCESS_REVOKED_USER_MESSAGE,
  GPT_NOT_READY_USER_MESSAGE,
  NOT_AUTHENTICATED_DETAIL,
  SESSION_EXPIRED_USER_MESSAGE,
  TOKEN_EXPIRED_DETAIL,
} from '@/apiClient/feErrorCodes'
import type {
  IChatMessage,
  IChatMessageUIHints,
  IChatProcessingStep,
  IChatToolCallHint,
} from '@/apiClient/types/chat'
import type { IUploadedFile } from '@/apiClient/types/file'
import { refreshKeycloakToken } from '@/main'
import { useAuthStore } from '@/store/auth'

const devLogger = (message: string, ...args: unknown[]) => {
  if (import.meta.env.DEV) {
    console.log(message, ...args)
  }
}

export type EventStreamOptions<TEventMap extends Record<string, unknown>> = {
  method?: 'GET' | 'POST'
  headers?: Record<string, string>
  body?: unknown
  /**
   * If true, attempts JSON.parse on data payloads.
   * If false, passes raw string to handlers.
   */
  parseJson?: boolean
  /**
   * Handlers keyed by SSE `event:` name.
   * Example: { messageChunk: (data) => {...} }
   */
  eventHandlers: {
    [K in keyof TEventMap]?: (payload: TEventMap[K]) => void
  }
  /**
   * Called for every event before specific handler.
   */

  onRawEvent?: (eventName: string | null, rawData: string) => void
  /**
   * When true, the stream is not aborted when the component that started it unmounts
   * (e.g. after `router.push` on `chat_created`). Use for new-chat SSE that must
   * keep receiving while navigating to the chat page.
   */
  persistAcrossNavigation?: boolean
  /**
   * When true, the request goes out WITHOUT an `Authorization` header, and both the
   * pre-send token refresh and the 401/403 refresh-retry are skipped. For endpoints
   * that accept anonymous callers (the public GPT chat): a logged-out visitor has no
   * token to attach, and `refreshKeycloakToken` sends the browser to the Keycloak
   * login page when a stale refresh token fails to renew — off a page whose whole
   * point is that it needs no account.
   */
  anonymous?: boolean
}

export type UseEventStreamReturn = {
  isStreaming: Readonly<Ref<boolean>>
  error: Readonly<Ref<Error | null>>
  /**
   * True when the stream ended because the client aborted it (the user pressed stop, or
   * the owning component unmounted) rather than because the server closed the body.
   *
   * Both endings resolve `promise` with no `error`, so without this flag a stream the
   * server cut short — a session timeout, a pod restart, a dropped proxy connection —
   * is indistinguishable from a deliberate stop, and from a successful one.
   */
  aborted: Readonly<Ref<boolean>>
  stop: () => void
  promise: Promise<void>
}

/**
 * Event stream event for chat messages
 */
export interface IChatStreamEvent {
  /** Chat ID */
  chat_id: string
  /** Message ID */
  message_id?: string
  /** Message content */
  content?: string
  /**
   * When set (non-empty), replaces the assistant message body instead of appending via the stream pump
   * (e.g. websocket job completion that mirrors GET chat message `content`).
   */
  content_replace?: string
  /** Model slug */
  model?: string
  /** Sources for web search */
  citations?: string[]
  /** Section/collapse hints; backend may send on the first `message_chunk`. */
  ui_hints?: IChatMessageUIHints
  /**
   * Names the pipeline step (`ui_hints.processing_steps[].step`) to remove from the target
   * assistant row, e.g. `pptx_generation` once its job delivered. Also drains buffered
   * `processing_meta` for this chat. The rest of `ui_hints` stays: other jobs' steps, and the
   * tool calls behind the reply.
   */
  clear_processing_step?: string
  /** Seconds since epoch; optional for patch-only events (e.g. websocket file completion). */
  timestamp?: number
  /** Files associated to the message */
  files?: IUploadedFile[]
  /** Hosted URL for a generated image (image-generation flow). */
  // url?: string
  /** Signed URL for a generated image (image-generation flow). */
  // signed_url?: string
  message?: IChatMessage
  /** Present on message_complete when the message has an associated background job (e.g. "pptx", "xlsx", "pdf"). */
  background_job_type?: string
  /** Present on message_complete for image_generation (ties assistant row to websocket job). */
  job_id?: string
  /** Present on `new_title` SSE events. */
  title?: string
}

/**
 * SSE `background_job_started` — one job a tool started for the reply (image, deck, spreadsheet,
 * PDF). A reply can start several; `message_complete` names only one of them.
 */
export interface IChatBackgroundJobStartedEvent {
  chat_id: string
  job_id: string
  job_type: string
  message_id?: string | null
  timestamp?: number
}

/** SSE `processing_meta` — same shape as {@link IChatProcessingStep}. */
export type IChatProcessingMetaEvent = IChatProcessingStep

/** Pre-stream milestone from SSE `stream_progress` events. */
export type ChatStreamPhase =
  'request_received' | 'context_ready' | 'calling_model'

export interface IChatStreamProgressEvent {
  chat_id: string
  phase: ChatStreamPhase
  timestamp?: number
}

/** Stream info event for chat messages */
export interface IChatStreamInfoEvent {
  action: 'stream_info'
  command: string
  message: string
  timestamp: number
  model: string
  chat_id: string
  message_index?: number
  message_id?: string
}

/** SSE `mcp_tool_call_start` / `mcp_tool_call_end` — one tool call's progress. */
export interface IChatToolCallEvent extends IChatToolCallHint {
  chat_id: string
  timestamp?: number
}

/** SSE `llm_retry` — the model request failed and is retried after `delay_seconds`. */
export interface IChatLlmRetryEvent {
  chat_id: string
  attempt: number
  max_attempts: number
  delay_seconds: number
  timestamp?: number
}

interface IChatContentFilterDetail {
  detected: boolean
  filtered: boolean
}

/**
 * SSE `content_filter` — the provider's prompt filter results. Sent whenever the provider
 * reports them, including when nothing was caught (`filters` then has no categories).
 */
export interface IChatContentFilterEvent {
  chat_id: string
  message_id?: string
  filters?: {
    protected_material?: IChatContentFilterDetail | null
    jailbreak?: IChatContentFilterDetail | null
  } | null
  timestamp?: number
}

/**
 * SSE `partial_context` — some of the GPT's knowledge files are still processing and were
 * left out. Sent first, so on a new chat it arrives before `chat_created` (no `chat_id`).
 */
export interface IChatPartialContextEvent {
  chat_id?: string | null
  total_files: number
  ready_files: number
  skipped_files: { file_id: string; status?: string | null }[]
  /** User-facing explanation. */
  message: string
  timestamp?: number
}

/**
 * SSE `stream_interrupted` — soft stop when the backend cancels an in-flight stream
 * (e.g. its streaming DB session timeout). Partial output stays; no `message_complete`
 * follows, so it must never be funnelled through the `error` path.
 */
export interface IChatStreamInterruptedEvent {
  /** Chat the interrupted stream belongs to. */
  chat_id: string
  /** User-facing explanation shown as a warning toast. */
  message?: string
  /** Machine-readable reason, e.g. `session_timeout`. */
  reason?: string
  /** Seconds since epoch. */
  timestamp?: number
}

/**
 * Composable for handling Server-Sent Events (SSE) streams
 * @param url - The endpoint URL for the SSE stream (relative or absolute)
 *              Relative URLs will be prefixed with VITE_API_ENDPOINT
 * @param options - Configuration options including event handlers
 * @example
 * const { isStreaming, error, stop } = useEventStream('/chat/stream', {
 *   eventHandlers: {
 *     message: (data) => console.log(data),
 *     error: (err) => console.error(err)
 *   }
 * })
 */
export function useEventStream<
  TEventMap extends Record<string, unknown> = Record<string, unknown>,
>(url: string, options: EventStreamOptions<TEventMap>): UseEventStreamReturn {
  const { persistAcrossNavigation = false, ...streamOptions } = options

  const isStreaming = ref(false)
  const error = ref<Error | null>(null)
  const aborted = ref(false)

  let controller: AbortController | null = null
  let promiseResolve: (() => void) | null = null

  let promiseReject: ((error: unknown) => void) | null = null

  const streamPromise = new Promise<void>((resolve, reject) => {
    promiseResolve = resolve
    promiseReject = reject
  })

  const stop = () => {
    if (controller) {
      controller.abort()
      controller = null
    }
    isStreaming.value = false
  }

  /**
   * Whether refreshing the token and retrying once is worth an attempt.
   *
   * Any `401` is: by definition the credential was missing, expired or rejected.
   * This used to also require `detail === 'Keycloak: Token has expired'`, which made
   * recovery hostage to one exact backend phrase — every other wording fell through
   * as a plain failure and the user's message simply never sent, on a page that is
   * often left open far longer than a token lives.
   *
   * `403` stays narrow: that is an authorization answer, and retrying a refusal only
   * doubles the request. The exception is the backend's own "not authenticated" 403.
   */
  const isAuthenticationError = async (
    response: Response,
  ): Promise<boolean> => {
    if (response.status === 401) return true
    if (response.status !== 403) return false

    try {
      const data = JSON.parse(await response.clone().text()) as {
        detail?: string
      }
      return data.detail === NOT_AUTHENTICATED_DETAIL
    } catch {
      return false
    }
  }

  const start = async (_isRetry = false) => {
    const {
      method = 'POST',
      headers = {},
      body,
      parseJson = true,
      eventHandlers,
      onRawEvent,
      anonymous = false,
    } = streamOptions

    stop() // cancel any previous stream
    /**
     * Held in a local as well as the shared `controller`, because everything from
     * here to `fetch` is now asynchronous (the token refresh below). `stop()` sets
     * the shared `controller` to null, so reading `controller.signal` after an await
     * would throw a TypeError — which is NOT an AbortError, so the catch below would
     * report the user's own "stop" as a failed stream and show them the raw message.
     * `stop()` aborts before it nulls, so the captured reference still carries the
     * abort and `fetch` rejects with the AbortError the catch already handles.
     */
    const requestController = new AbortController()
    controller = requestController
    isStreaming.value = true
    error.value = null
    // Reset after the `stop()` above, so a token-refresh retry doesn't inherit its abort.
    aborted.value = false

    const baseUrl = import.meta.env.VITE_API_ENDPOINT
    const fullUrl = url.startsWith('http') ? url : `${baseUrl}${url}`

    // Renew a token that is about to lapse BEFORE sending, exactly as every axios
    // client does in its request interceptor. Without this the stream is the one
    // caller that ships a stale bearer and finds out from a 401 — a wasted round
    // trip on a page that stays open for long stretches. Cheap when the token is
    // still fresh (the adapter returns immediately) and never fatal: a refresh that
    // fails leaves the stored token in place and the retry below still applies.
    if (!anonymous) {
      try {
        await refreshKeycloakToken()
      } catch {
        /* keep the stored token; the post-response retry is the remaining net */
      }
    }

    const requestHeaders: Record<string, string> = {
      Accept: 'text/event-stream',
      'Content-Type': 'application/json',
    }
    // Anonymous streams send no bearer at all — the endpoint does not ask for one,
    // and `Bearer undefined` is worse than an absent header.
    if (!anonymous) {
      requestHeaders.Authorization = `Bearer ${useAuthStore().accessToken}`
    }
    // Caller-supplied headers keep last-write precedence (matches previous behaviour).
    Object.assign(requestHeaders, headers)

    let response: Response
    try {
      response = await fetch(fullUrl, {
        method,
        headers: requestHeaders,
        body: body != null ? JSON.stringify(body) : undefined,
        signal: requestController.signal,
      })
    } catch (e: unknown) {
      isStreaming.value = false
      const err = e instanceof Error ? e : new Error(String(e))
      // AbortError is not treated as a failure (user intentionally stopped the stream)
      if (err.name === 'AbortError') {
        aborted.value = true
        controller = null
        promiseResolve?.()
        return
      }
      error.value = err
      controller = null
      promiseReject?.(err)
      return
    }

    // Check for authentication error and retry once — never for an anonymous stream,
    // which has no token to refresh and would only send the same request twice.
    if (!_isRetry && !anonymous && (await isAuthenticationError(response))) {
      try {
        // A 401 means the token itself was rejected, whatever the adapter thinks of it.
        await refreshKeycloakToken({ force: response.status === 401 })
        // Retry the request with refreshed token
        return start(true)
      } catch (refreshError) {
        isStreaming.value = false
        const err =
          refreshError instanceof Error
            ? refreshError
            : new Error('Token refresh failed')
        error.value = err
        controller = null
        promiseReject?.(err)
        return
      }
    }

    if (!response.ok || !response.body) {
      isStreaming.value = false
      let message = `HTTP ${response.status}: ${response.statusText}`
      let streamErrorCode: string | undefined
      try {
        const text = await response.clone().text()
        if (text) {
          const data = JSON.parse(text) as {
            detail?: string
            fe_error_code?: string
            error_message?: string
          }
          if (data.fe_error_code === FE_ERROR_GPT_NOT_READY) {
            streamErrorCode = FE_ERROR_GPT_NOT_READY
            message =
              data.error_message?.trim() ||
              data.detail?.trim() ||
              GPT_NOT_READY_USER_MESSAGE
          } else if (data.fe_error_code === FE_ERROR_GPT_ACCESS_REVOKED) {
            streamErrorCode = FE_ERROR_GPT_ACCESS_REVOKED
            message =
              data.error_message?.trim() ||
              data.detail?.trim() ||
              GPT_ACCESS_REVOKED_USER_MESSAGE
          } else if (
            typeof data.detail === 'string' &&
            data.detail.trim() === GPT_ACCESS_REVOKED_USER_MESSAGE
          ) {
            streamErrorCode = FE_ERROR_GPT_ACCESS_REVOKED
            message = GPT_ACCESS_REVOKED_USER_MESSAGE
          } else if (data.detail === TOKEN_EXPIRED_DETAIL) {
            message = SESSION_EXPIRED_USER_MESSAGE
          } else if (typeof data.detail === 'string' && data.detail.trim()) {
            message = data.detail.trim()
          } else if (
            typeof data.error_message === 'string' &&
            data.error_message.trim()
          ) {
            message = data.error_message.trim()
          }
        }
      } catch {
        // keep default HTTP message
      }
      const err = new Error(message) as Error & { streamErrorCode?: string }
      if (streamErrorCode) err.streamErrorCode = streamErrorCode
      error.value = err
      controller = null
      promiseReject?.(err)
      return
    }

    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''

    try {
      while (true) {
        const { value, done } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })

        // SSE messages separated by blank line
        const events = buffer.split('\n\n')
        buffer = events.pop() ?? ''

        for (const rawEvent of events) {
          if (!rawEvent.trim()) continue

          let eventName: string | null = null
          const dataLines: string[] = []

          for (const line of rawEvent.split('\n')) {
            if (line.startsWith(':')) {
              // comment, ignore
              continue
            }
            if (line.startsWith('event:')) {
              eventName = line.slice(6).trim()
            } else if (line.startsWith('data:')) {
              dataLines.push(line.slice(5).trim())
            }
          }

          const rawData = dataLines.join('\n')
          onRawEvent?.(eventName, rawData)

          // No handler? skip.
          // If no eventName, treat as "message" by convention
          const key = (eventName ?? 'message') as keyof TEventMap
          const handler = eventHandlers[key]
          if (!handler) {
            devLogger(
              `[useEventStream] No handler for event: "${String(key)}"`,
              rawData,
            )
            continue
          }

          let payload: unknown = rawData
          if (parseJson && rawData) {
            try {
              payload = JSON.parse(rawData)
            } catch {
              // leave as string if JSON.parse fails
            }
          }

          handler(payload as TEventMap[keyof TEventMap])
        }
      }
    } catch (e: unknown) {
      if (e instanceof Error && e.name !== 'AbortError') {
        error.value = e
        promiseReject?.(e)
      } else {
        // AbortError is not treated as a failure
        aborted.value = true
        promiseResolve?.()
      }
    } finally {
      isStreaming.value = false
      controller = null
      // If we completed without error, resolve the promise
      if (!error.value) {
        promiseResolve?.()
      }
    }
  }

  if (getCurrentInstance() && !persistAcrossNavigation) {
    onBeforeUnmount(() => stop())
  }

  // Auto-start the stream
  start()

  return {
    isStreaming: readonly(isStreaming),
    error: readonly(error),
    aborted: readonly(aborted),
    stop,
    promise: streamPromise,
  }
}
